import { useQueries, type Query } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';

import { isFailedStatus, isTerminalStatus } from '../constants';
import { getAnalyticsSyncStatus } from '../services/analytics-sync.service';
import { useAnalyticsSyncStore, type SyncSession } from '../store/analytics-sync.store';
import type {
  AnalyticsSyncStatusResponse,
  AnalyticsWorkflowStatus,
} from '../types/analytics-sync.types';

import { AnalyticsSyncToast, type AnalyticsSyncToastRow } from './analytics-sync-toast';

/** Duration (ms) the terminal success/error toast lingers before auto-dismiss. */
const FINAL_TOAST_DURATION = 6000;

/**
 * App-level watcher for analytics-sync sessions. Mounted once in the tenant
 * layout so its polling + toast survive navigation between pages. Renders one
 * headless runner per active session; renders nothing visible itself.
 */
export function AnalyticsSyncManager() {
  const sessions = useAnalyticsSyncStore((s) => s.sessions);
  return (
    <>
      {Object.values(sessions).map((session) => (
        <SyncSessionRunner key={session.id} session={session} />
      ))}
    </>
  );
}

/**
 * Polls every workflow in one session (via `useQueries`, 3s cadence until
 * terminal) and drives a single Sonner toast in place. On all-terminal it
 * flips the toast to success/error and clears the session from the store.
 */
function SyncSessionRunner({ session }: { session: SyncSession }) {
  const { t } = useTranslation('experiments');
  const endSession = useAnalyticsSyncStore((s) => s.endSession);
  const finalizedRef = useRef(false);
  // Collapsed state lives here (not in the toast body) so it survives the
  // re-emit on every poll — an inner useState would reset each tick.
  const [collapsed, setCollapsed] = useState(false);

  const results = useQueries({
    queries: session.workflows.map((w) => ({
      queryKey: queryKeys.analyticsSync.status(w.workflowId),
      queryFn: () => getAnalyticsSyncStatus(w.workflowId),
      // Poll while non-terminal; stop on a terminal status or a hard error so a
      // failing poll can't spin forever (the 404-after-trigger race is covered
      // by TanStack Query's default retry before the error surfaces here).
      refetchInterval: (query: Query<AnalyticsSyncStatusResponse>) => {
        if (query.state.status === 'error') return false;
        return isTerminalStatus(query.state.data?.status) ? false : 3000;
      },
    })),
  });

  // Live per-connector view derived from the polls (not the store) so the toast
  // reflects the freshest data; an errored poll degrades that connector to FAILED.
  const rows: AnalyticsSyncToastRow[] = session.workflows.map((w, i) => {
    const r = results[i];
    const errored = r?.status === 'error';
    const status: AnalyticsWorkflowStatus = errored ? 'FAILED' : (r?.data?.status ?? w.status);
    const rawPct = r?.data?.progress?.percentComplete;
    const percent = status === 'COMPLETED' ? 100 : typeof rawPct === 'number' ? rawPct : 0;
    return { connectorCode: w.connectorCode, status, percent };
  });

  const overallPercent =
    rows.length === 0 ? 0 : rows.reduce((acc, row) => acc + row.percent, 0) / rows.length;
  const allDone = rows.length > 0 && rows.every((row) => isTerminalStatus(row.status));
  const anyFailed = rows.some((row) => isFailedStatus(row.status));

  // Signature of the rendered state — effect re-runs (and re-emits the toast)
  // only when a status or percent actually changes.
  const signature = useMemo(
    () =>
      rows.map((r) => `${r.connectorCode}:${r.status}:${String(Math.round(r.percent))}`).join('|'),
    [rows],
  );

  const { experimentName, toastId, id: sessionId } = session;

  useEffect(() => {
    if (allDone) {
      if (finalizedRef.current) return;
      finalizedRef.current = true;
      if (anyFailed) {
        toast.error(t('detail.syncAnalytics.toast.error'), {
          id: toastId,
          duration: FINAL_TOAST_DURATION,
        });
      } else {
        toast.success(t('detail.syncAnalytics.toast.success', { name: experimentName }), {
          id: toastId,
          duration: FINAL_TOAST_DURATION,
        });
      }
      endSession(sessionId);
      return;
    }
    toast(
      <AnalyticsSyncToast
        experimentName={experimentName}
        rows={rows}
        overallPercent={overallPercent}
        collapsed={collapsed}
        onToggleCollapse={() => {
          setCollapsed((c) => !c);
        }}
      />,
      // `closeButton: false` overrides the global Toaster default — the toast is
      // dismissed via completion or the collapse toggle, not a manual close.
      // `flex-1 min-w-0` stretches Sonner's content wrapper to the toast's full
      // width — without it the wrapper shrinks to its widest text line, leaving
      // the progress bar and rows floating in empty space.
      {
        id: toastId,
        duration: Infinity,
        closeButton: false,
        classNames: { content: 'flex-1 min-w-0' },
      },
    );
    // `rows`/`overallPercent` are folded into `signature`; listing them would
    // re-run on every render without changing the emitted toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, allDone, anyFailed, toastId, sessionId, experimentName, endSession, t, collapsed]);

  return null;
}
